import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import UserCard from "./UserCard";
import { useAuth } from "../../context/AuthContext";
import { userApi } from "../../api/user";
import toast, { Toaster } from "react-hot-toast";
import { Pagination } from 'flowbite-react';

const formatRole = (roleName) => {
  return roleName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

export default function UserTabs({ 
  tabsData, 
  users, 
  onRoleChange, 
  selectedRole, 
  user, 
  onUserUpdate, 
  onToggleShowWin,
  page,
  totalPages,
  onPageChange,
  loading,
  onOpenActiveGames,
  onUserRoleUpgrade
}) {
  const { tokens } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [hasMore, setHasMore] = useState(true);

  const activeTab = tabsData.findIndex(tab => tab.role === selectedRole);

  // Filter users based on selected status
  const filteredUsers = users.filter(user => 
    selectedStatus === "All" ? true : (selectedStatus === "Active" ? user.isActive : !user.isActive)
  );

  const statusOptions = ["All", "Active", "Inactive"];

  const handleToggleStatus = async (userId, newStatus) => {
    try {
      const response = await userApi.updateUser(userId, tokens.access.token, { 
        active: newStatus 
      });
      
      if (response.status === 200) {
        onUserUpdate({ id: userId, isActive: newStatus });
        toast.success('Status updated successfully', 
          {
            duration: 2000,
            position: "bottom-center",
            style: {
              background: "#333",
              color: "#fff",
            },
          }
        );
      } else {
        console.error('Failed to update status:', response.data);
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  return (
    <div className="px-4 pt-3 pb-16">
      {/* Tabs Navigation */}
      <div className="flex justify-between gap-2 py-2">
        <div className="flex space-x-1.5 text-xs overflow-x-auto whitespace-nowrap hide-scrollbar">
          {tabsData.map((tab, index) => (
            tab.role > user.roleId || tab.role === "" ? (
              <button
                key={index}
                className={`flex space-x-1.5 text-xs max-xs:text-[10px] ${
                  activeTab === index
                    ? "bg-[#65758B] p-2.5 max-xs:p-2 rounded-md text-white"
                    : "bg-[#E1E7EF] p-2.5 max-xs:p-2 rounded-md text-[#65758B]"
                }`}
                onClick={() => onRoleChange(tab.role)}
              >
                {tab.title}
              </button>
            ) : null
          ))}
        </div> 

        <div>
          <div className="relative w-full">
            <button
              id="dropdownButton"
              className="w-full p-2 text-xs font-normal text-gray-700 bg-white border rounded-md shadow-sm flex justify-between items-center"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <span>{selectedStatus}</span>
              <span className="text-gray-500 ms-3">
                {dropdownOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </span>
            </button>
            {dropdownOpen && (
              <ul className="absolute left-0 z-10 w-full mt-1 bg-white border rounded-md shadow-md">
                {statusOptions.map((status, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      setSelectedStatus(status);
                      setDropdownOpen(false);
                    }}
                    className={`p-2 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer flex justify-between items-center ${
                      index !== 0 ? "border-t border-[#E1E7EF]" : ""
                    }`}
                  >
                    {status}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div
        className="space-y-3 overflow-y-auto hide-scrollbar"
        style={{ minHeight: "calc(100vh - 250px)", maxHeight: "calc(100vh - 250px)" }}
      >
        {loading && filteredUsers.length === 0 ? (
          <div className="text-center py-4">Loading users...</div>
        ) : filteredUsers.length > 0 ? (
          filteredUsers.map((user, idx) => (
            <UserCard
              key={user.id}
              {...user}
              role={formatRole(user.role.name)}
              roleId={user.role.id}
              onToggleStatus={handleToggleStatus}
              onToggleShowWin={onToggleShowWin}
              onOpenActiveGames={onOpenActiveGames}
              selectedRole={selectedRole}
              onUserRoleUpgrade={onUserRoleUpgrade}
            />
          ))
        ) : (
          <p className="text-gray-500 text-sm text-center mt-3">
            No users found for the selected filter.
          </p>
        )}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4 pb-4">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
            showIcons={true}
            layout="pagination"
            className="text-sm"
          />
        </div>
      )}
      <Toaster />
    </div>
  );
}
